import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import CurrencyFormat from "react-currency-format";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { useDeleteProjectCreditMutation } from "@generated";
import useListProjectCredits from "../api/useListProjectCredits";
import { ProjectCredit, PaymentCreditOption } from "../types/ProjectCredit";
import useStyles from "../styles/useStyles";

interface Props {
  projectId: string;
  projectStatus: string;
  permission: string;
  currencyUnit: string;
  paymentCreditsMap: Record<
    string,
    { paymentSource: string; description: string; criteria: string }
  >;
}

const Show: React.FC<Props> = ({
  projectId,
  projectStatus,
  permission,
  currencyUnit,
  paymentCreditsMap,
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [errorMessages, setErrorMessages] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteCredit] = useDeleteProjectCreditMutation();

  const {
    projectCredits,
    projectCreditsLoading,
    projectCreditsFetching,
    pageMeta,
    refetch,
  } = useListProjectCredits({
    projectId,
    paymentCreditsMap,
    page: { number: pageNumber, size: pageSize },
  });

  useEffect(() => {
    window.history.replaceState({}, "");
    refetch();
    if (location?.state?.edit) {
      setSuccessMessage("Payment credit updated successfully!");
      setShowSuccessAlert(true);
    }
    if (location?.state?.new) {
      setSuccessMessage("Payment credit created successfully!");
      setShowSuccessAlert(true);
    }
  }, [pageNumber]);

  const handleDelete = () => {
    setDeleteQueue([...selectedIds]);
    setShowConfirmationModal(true);
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    if (deleteQueue.length === 0) return;

    const promises = deleteQueue.map((id) =>
      deleteCredit({
        slug: accountSlug,
        id: Number(id),
      })
    );

    Promise.all(promises)
      .then(() => {
        setSuccessMessage(
          `Payment credit${deleteQueue.length > 1 ? "s" : ""} deleted successfully!`
        );
        setShowSuccessAlert(true);
        setIsDeleting(false);
        setShowConfirmationModal(false);
        setSelectedIds([]);
        setDeleteQueue([]);
        refetch();
      })
      .catch(() => {
        setErrorMessages(
          "One or more payment credits could not be deleted. Please try again later."
        );
        setShowFailAlert(true);
        setIsDeleting(false);
      });
  };

  const getConfirmationMessage = () => (
    <div style={{ color: "#1C2655", fontSize: "14px" }}>
      {isDeleting ? (
        <SmallSpinner />
      ) : (
        <span style={{ fontWeight: "700" }}>
          {`You are about to delete ${selectedIds.length} payment credit${
            selectedIds.length > 1 ? "s" : ""
          }.`}
        </span>
      )}
      <ul style={{ marginTop: "10px" }}>
        {selectedIds.map((id) => {
          const credit = projectCredits.find((c) => c.id === id);
          return (
            <li key={id}>
              {credit?.paymentSource}
              {credit?.description ? ` / ${credit.description}` : ""}
            </li>
          );
        })}
      </ul>
      This action cannot be undone. Do you wish to proceed?
    </div>
  );

  const total = projectCredits.reduce((sum, c) => sum + c.amount, 0);

  const columns: ColumnDef<ProjectCredit>[] = [
    {
      header: "Source/Reason",
      cell(props) {
        return (
          <div
            className={styles.editLink}
            onClick={() => {
              navigate(
                `/projects/${projectId}/project_credits/edit/${props.row.original.id}`,
                { state: { credit: props.row.original } }
              );
            }}
          >
            {props.row.original.paymentSource}{" "}
            <FontAwesomeIcon icon={faPencil} />
          </div>
        );
      },
    },
    {
      header: "Description",
      cell(props) {
        return <span>{props.row.original.description}</span>;
      },
    },
    {
      header: "Amount",
      cell(props) {
        return (
          <span className={styles.amountCell}>
            <CurrencyFormat
              displayType="text"
              prefix={currencyUnit}
              isNumericString={true}
              thousandSeparator={true}
              value={props.row.original.amount.toFixed(2)}
            />
          </span>
        );
      },
    },
    {
      header: "Criteria",
      cell(props) {
        return <span>{props.row.original.criteria}</span>;
      },
    },
  ];

  if (permission === "none") {
    return (
      <div className={styles.page}>
        You do not have permission to view this page. Please contact your
        administrator for assistance.
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <Card className={styles.card}>
        <Card.Header className={styles.cardHeader}>
          <Row className={styles.headerRow}>
            <Col className={styles.title}>Payment Credits</Col>
            {projectStatus === "building" &&
              (permission === "create" || permission === "manage") && (
              <Col>
                <Button
                  className="squareGreenButton floatRight"
                  onClick={() =>
                    navigate(
                      `/projects/${projectId}/project_credits/new`,
                      { state: {} }
                    )
                  }
                >
                  Add Credit
                </Button>
              </Col>
            )}
          </Row>
        </Card.Header>
        <hr className={styles.hr} />
        <Card.Body>
          {selectedIds.length > 0 && (
            <Row className="mb-2">
              <Col sm={3}>
                <Button className="deleteBox" onClick={handleDelete}>
                  {selectedIds.length} Selected{" "}
                  <span className="selectedSpan"></span>
                  <FontAwesomeIcon
                    style={{ color: "black" }}
                    icon={faTrashCan}
                  />
                </Button>
              </Col>
            </Row>
          )}
          {projectCreditsLoading || projectCreditsFetching ? (
            <ScopeStackSpinner />
          ) : (
            <>
              {projectCredits.length === 0 ? (
                <p>No payment credits applied to this project</p>
              ) : (
                <>
                  <DataTable
                    data={projectCredits}
                    columns={columns}
                    selectable={
                      permission === "manage" &&
                      projectStatus === "building"
                    }
                    striped
                    hover
                    totalPages={pageMeta.pageCount}
                    totalRows={pageMeta.recordCount}
                    currentPage={pageNumber}
                    setCurrentPage={setPageNumber}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    paginationEnabled
                    pageSizeEnabled
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                  />
                  <Row className={styles.totalRow}>
                    <Col>TOTAL</Col>
                    <Col></Col>
                    <Col className={styles.amountCell}>
                      {currencyUnit}
                      {total.toFixed(2)}
                    </Col>
                    <Col></Col>
                  </Row>
                </>
              )}
            </>
          )}
          <ScopeStackModal
            modalTitle="Delete Payment Credit"
            modalBody={getConfirmationMessage()}
            handleButton2Click={isDeleting ? () => null : confirmDelete}
            handleClose={() => {
              setShowConfirmationModal(false);
              setDeleteQueue([]);
            }}
            show={showConfirmationModal}
            handleButton1Click={() => {
              setShowConfirmationModal(false);
              setDeleteQueue([]);
            }}
            deleteModal={true}
            button1Text="Cancel"
            button2Text="Delete"
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default Show;
